import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { ApiError } from '@/lib/api/client';
import { toast } from '@/hooks/useToast';
import { useBillPayments } from './useBillPayments';
import { useRecordPayment } from './useRecordPayment';
import type { Bill, PaymentMode } from './types';

/**
 * Replaces the old bare "✓ Mark paid" button (a status flip with no amount,
 * mode or actor recorded — ISS-19). Defaults to the full balance due, but
 * supports a partial payment: the bill moves to 'Partially Paid' until the
 * sum of all payments reaches total_amount, matching
 * utils/billingPayments.js's own status derivation.
 *
 * The `Bill` row from the list has no running balance on it — only
 * total_amount — so a bill already Partially Paid needs its payment history
 * to know what's actually still owed (total_amount alone would re-offer the
 * FULL amount as if nothing had been paid yet).
 */
export default function RecordPaymentDialog({ bill, onClose }: { bill: Bill; onClose: () => void }) {
  const total = parseFloat(bill.total_amount);
  const paymentsQuery = useBillPayments(bill.id);
  const alreadyPaid = (paymentsQuery.data ?? []).reduce((s, p) => s + parseFloat(p.amount), 0);
  const balance = Math.max(0, Math.round((total - alreadyPaid) * 100) / 100);

  const [amount, setAmount] = useState(String(total));
  const [mode, setMode] = useState<PaymentMode>(bill.payment_mode);
  const [reference, setReference] = useState('');

  // The balance isn't known until the payment history loads — sync the
  // pre-filled amount once it does, rather than always showing total_amount.
  useEffect(() => {
    if (!paymentsQuery.isLoading) setAmount(String(balance));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsQuery.isLoading]);

  const recordPayment = useRecordPayment(bill.id);
  const amountNum = parseFloat(amount);
  const valid = Number.isFinite(amountNum) && amountNum > 0;

  const submit = () => {
    if (!valid) return;
    recordPayment.mutate(
      { amount: amountNum, payment_mode: mode, reference_number: reference.trim() || undefined },
      {
        onSuccess: ({ payment }) => {
          toast({ variant: 'success', title: payment.receipt_no ? `Receipt ${payment.receipt_no}` : 'Payment recorded', description: `₹${parseFloat(payment.amount).toFixed(2)} · ${payment.payment_mode}` });
          onClose();
        },
      }
    );
  };

  const error = recordPayment.error instanceof ApiError ? recordPayment.error.message : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-110">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
        </DialogHeader>

        <div className="text-[13px] text-ink-muted">
          {bill.bill_number} · Balance due{' '}
          <span className="font-semibold text-ink">{paymentsQuery.isLoading ? '…' : `₹${balance.toFixed(2)}`}</span>
          {alreadyPaid > 0 && !paymentsQuery.isLoading && (
            <span className="text-ink-muted"> (₹{alreadyPaid.toFixed(2)} already paid of ₹{total.toFixed(2)})</span>
          )}
        </div>

        {error && <div className="rounded-(--radius) bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

        <div className="grid grid-cols-2 gap-2.5">
          <FormField label="Amount (₹)">
            <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </FormField>
          <FormField label="Payment mode">
            <NativeSelect value={mode} onChange={(e) => setMode(e.target.value as PaymentMode)}>
              {(['Cash', 'UPI', 'Card'] as const).map((m) => (
                <option key={m}>{m}</option>
              ))}
            </NativeSelect>
          </FormField>
        </div>
        <FormField label="Reference number (optional)">
          <Input placeholder="UPI txn id, cheque no…" value={reference} onChange={(e) => setReference(e.target.value)} />
        </FormField>

        {valid && amountNum < balance && (
          <div className="text-[12px] text-warn-ink">
            This is a partial payment — ₹{(balance - amountNum).toFixed(2)} will remain due.
          </div>
        )}

        {(paymentsQuery.data?.length ?? 0) > 0 && (
          <div>
            <div className="mb-1 text-[12px] font-semibold text-ink">Payments so far</div>
            <ul className="divide-y divide-border rounded-(--radius) border border-border text-[12px]">
              {paymentsQuery.data!.map((p) => (
                <li key={p.id} className="flex flex-wrap items-baseline justify-between gap-x-3 px-2.5 py-1.5">
                  <span className="text-ink">
                    ₹{parseFloat(p.amount).toFixed(2)} · {p.payment_mode}
                    {p.receipt_no && <span className="ml-1.5 font-mono text-[11px] text-ink-muted">{p.receipt_no}</span>}
                  </span>
                  <span className="text-ink-muted">
                    {new Date(p.received_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    {(p.received_by_name || p.received_by) && ` · ${p.received_by_name || p.received_by}`}
                    {p.balance_after != null && ` · balance ₹${parseFloat(p.balance_after).toFixed(2)}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={!valid || paymentsQuery.isLoading || recordPayment.isPending} loading={recordPayment.isPending} onClick={submit}>
            {recordPayment.isPending ? 'Recording…' : `Record ₹${Number.isFinite(amountNum) ? amountNum.toFixed(2) : '0.00'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
